// Noyau — forme des nœuds et arithmétique rationnelle exacte.
// Le noyau ne connaît AUCUN type de nœud métier : Add, Mul, Eq… viennent des
// packs (base de données). Il ne connaît que trois formes :
//   { t: 'Num', p, q, dec? }   rationnel p/q (q > 0, réduit) ; dec = écriture décimale d'origine
//   { t: 'Sym', name }         identifiant ou constante ("x", "v₀", "π")
//   { t: 'Var', name }         variable de motif ("?a") — uniquement dans les règles
//   { t: <Type>, k: [...], name? }   nœud composé ; name sert aux appels de fonction (Call)

export const num = (p, q = 1) => {
  if (q === 0) throw new Error('division_by_zero');
  if (q < 0) {
    p = -p;
    q = -q;
  }
  const g = gcd(Math.abs(p), q) || 1;
  return { t: 'Num', p: p / g, q: q / g };
};
export const sym = (name) => ({ t: 'Sym', name });
export const pvar = (name) => ({ t: 'Var', name });
export const node = (t, k, extra) => (extra ? { t, k, ...extra } : { t, k });

export function gcd(a, b) {
  while (b) {
    [a, b] = [b, a % b];
  }
  return a;
}

export const isNum = (n) => n && n.t === 'Num';
export const isSym = (n) => n && n.t === 'Sym';
export const isVar = (n) => n && n.t === 'Var';
export const isLeaf = (n) => n.t === 'Num' || n.t === 'Sym' || n.t === 'Var';

// ── Rationnels ────────────────────────────────────────────────────────────
export const Q = {
  add: (a, b) => num(a.p * b.q + b.p * a.q, a.q * b.q),
  sub: (a, b) => num(a.p * b.q - b.p * a.q, a.q * b.q),
  mul: (a, b) => num(a.p * b.p, a.q * b.q),
  div: (a, b) => num(a.p * b.q, a.q * b.p),
  neg: (a) => num(-a.p, a.q),
  pow: (a, n) => {
    // n entier
    if (n === 0) return num(1);
    if (n < 0) return Q.pow(num(a.q, a.p), -n);
    return num(a.p ** n, a.q ** n);
  },
  eq: (a, b) => a.p === b.p && a.q === b.q,
  cmp: (a, b) => Math.sign(a.p * b.q - b.p * a.q),
  isInt: (a) => a.q === 1,
  isZero: (a) => a.p === 0,
  val: (a) => a.p / a.q,
};

// ── Structure ─────────────────────────────────────────────────────────────
// Égalité structurelle stricte (pas de commutativité : voir kernel/rewrite.js).
export function equal(a, b) {
  if (a === b) return true;
  if (!a || !b || a.t !== b.t) return false;
  switch (a.t) {
    case 'Num':
      return a.p === b.p && a.q === b.q;
    case 'Sym':
    case 'Var':
      return a.name === b.name;
    default:
      if (a.name !== b.name || a.k.length !== b.k.length) return false;
      for (let i = 0; i < a.k.length; i++) if (!equal(a.k[i], b.k[i])) return false;
      return true;
  }
}

// Clé textuelle stable, utile pour trier / hacher / dédoublonner.
export function key(n) {
  switch (n.t) {
    case 'Num':
      return n.q === 1 ? `${n.p}` : `${n.p}/${n.q}`;
    case 'Sym':
      return n.name;
    case 'Var':
      return `?${n.name}`;
    default:
      return `${n.t}${n.name ? ':' + n.name : ''}(${n.k.map(key).join(',')})`;
  }
}

export function map(n, f) {
  // post-ordre : f reçoit un nœud dont les enfants sont déjà transformés
  if (isLeaf(n)) return f(n);
  const k = n.k.map((c) => map(c, f));
  return f({ ...n, k });
}

export function* walk(n, path = []) {
  // pré-ordre, avec le chemin d'indices
  yield [n, path];
  if (!isLeaf(n)) for (let i = 0; i < n.k.length; i++) yield* walk(n.k[i], [...path, i]);
}

export function getAt(n, path) {
  for (const i of path) n = n.k[i];
  return n;
}
export function setAt(n, path, v) {
  if (!path.length) return v;
  const [i, ...rest] = path;
  const k = n.k.slice();
  k[i] = setAt(n.k[i], rest, v);
  return { ...n, k };
}

export function symbols(n, out = new Set()) {
  if (n.t === 'Sym') out.add(n.name);
  else if (!isLeaf(n)) n.k.forEach((c) => symbols(c, out));
  return out;
}
